/**
 * useAnalytics Hook
 * Provides access to analytics tracking methods
 */

import { useCallback, useEffect } from 'react';
import { analyticsService, sessionTracker } from '../services/analytics/core';
import type {
  InstrumentSelectionData,
  InstrumentChangeData,
  PresetApplicationData,
  ParameterAdjustmentData,
  ParameterChangeCompleteData,
  CustomParametersToggleData,
  GridCellToggleData,
  NotePitchChangeData,
  GridDragOperationData,
  RowActionData,
  PlaybackStartData,
  PlaybackStopData,
  BPMChangeData,
  CompositionExportData,
  CompositionSaveLoadData,
  AIGenerationStartData,
  AIGenerationCompleteData,
  AIGenerationErrorData,
  VolumeAdjustmentData,
  MuteToggleData,
  TemplateLoadData,
  TemplateSaveData,
} from '../services/analytics/types';

export interface UseAnalyticsReturn {
  // Session tracking
  startSession: (userType?: 'authenticated' | 'anonymous', userId?: string) => void;
  endSession: () => void;
  
  // Instrument tracking
  trackInstrumentSelection: (data: InstrumentSelectionData) => void;
  trackInstrumentChange: (data: InstrumentChangeData) => void;
  trackPresetApplication: (data: PresetApplicationData) => void;
  
  // Parameter tracking
  trackParameterAdjustment: (data: ParameterAdjustmentData) => void;
  trackParameterChangeComplete: (data: ParameterChangeCompleteData) => void;
  trackCustomParametersToggle: (data: CustomParametersToggleData) => void;
  
  // Grid tracking
  trackGridCellToggle: (data: GridCellToggleData) => void;
  trackNotePitchChange: (data: NotePitchChangeData) => void;
  trackGridDragOperation: (data: GridDragOperationData) => void;
  trackRowAction: (data: RowActionData) => void;
  
  // Playback tracking
  trackPlaybackStart: (data: PlaybackStartData) => void;
  trackPlaybackStop: (data: PlaybackStopData) => void;
  trackBPMChange: (data: BPMChangeData) => void;
  
  // Composition tracking
  trackCompositionExport: (data: CompositionExportData) => void;
  trackCompositionSaveLoad: (data: CompositionSaveLoadData) => void;
  
  // AI tracking
  trackAIGenerationStart: (data: AIGenerationStartData) => void;
  trackAIGenerationComplete: (data: AIGenerationCompleteData) => void;
  trackAIGenerationError: (data: AIGenerationErrorData) => void;
  
  // Mixing tracking
  trackVolumeAdjustment: (data: VolumeAdjustmentData) => void;
  trackMuteToggle: (data: MuteToggleData) => void;
  
  // Template tracking
  trackTemplateLoad: (data: TemplateLoadData) => void;
  trackTemplateSave: (data: TemplateSaveData) => void;
  
  // Privacy controls
  setOptOut: (optOut: boolean) => void;
  getOptOutStatus: () => boolean;
  
  // Health monitoring
  getHealthMetrics: () => ReturnType<typeof analyticsService.getHealthMetrics>;
}

/**
 * Hook for accessing analytics tracking functionality
 */
export function useAnalytics(): UseAnalyticsReturn {
  // Start session on mount
  useEffect(() => {
    sessionTracker.startSession('anonymous');
    
    return () => {
      sessionTracker.endSession();
    };
  }, []);

  // Session tracking
  const startSession = useCallback((userType: 'authenticated' | 'anonymous' = 'anonymous', userId?: string) => {
    sessionTracker.startSession(userType, userId);
  }, []);

  const endSession = useCallback(() => {
    sessionTracker.endSession();
  }, []);

  // Instrument tracking
  const trackInstrumentSelection = useCallback((data: InstrumentSelectionData) => {
    analyticsService.trackInstrumentSelection(data);
    sessionTracker.recordActivity('gridEditing');
    sessionTracker.updateMetrics({ instrumentsUsed: new Set([data.instrumentType]) });
  }, []);

  const trackInstrumentChange = useCallback((data: InstrumentChangeData) => {
    analyticsService.trackInstrumentChange(data);
    sessionTracker.recordActivity('gridEditing');
    sessionTracker.updateMetrics({ instrumentsUsed: new Set([data.newInstrumentType]) });
  }, []);

  const trackPresetApplication = useCallback((data: PresetApplicationData) => {
    analyticsService.trackPresetApplication(data);
    sessionTracker.recordActivity('parameterAdjustment');
  }, []);

  // Parameter tracking
  const trackParameterAdjustment = useCallback((data: ParameterAdjustmentData) => {
    analyticsService.trackParameterAdjustment(data);
    sessionTracker.recordActivity('parameterAdjustment');
    sessionTracker.updateMetrics({ parametersAdjusted: 1 });
  }, []);

  const trackParameterChangeComplete = useCallback((data: ParameterChangeCompleteData) => {
    analyticsService.trackParameterChangeComplete(data);
    sessionTracker.recordActivity('parameterAdjustment');
  }, []);

  const trackCustomParametersToggle = useCallback((data: CustomParametersToggleData) => {
    analyticsService.trackCustomParametersToggle(data);
    sessionTracker.recordActivity('parameterAdjustment');
  }, []);

  // Grid tracking
  const trackGridCellToggle = useCallback((data: GridCellToggleData) => {
    analyticsService.trackGridCellToggle(data);
    sessionTracker.recordActivity('gridEditing');
    if (data.activeState) {
      sessionTracker.updateMetrics({ cellsActivated: 1 });
    }
  }, []);

  const trackNotePitchChange = useCallback((data: NotePitchChangeData) => {
    analyticsService.trackNotePitchChange(data);
    sessionTracker.recordActivity('gridEditing');
  }, []);

  const trackGridDragOperation = useCallback((data: GridDragOperationData) => {
    analyticsService.trackGridDragOperation(data);
    sessionTracker.recordActivity('gridEditing');
    sessionTracker.updateMetrics({ cellsActivated: data.cellsAffected });
  }, []);

  const trackRowAction = useCallback((data: RowActionData) => {
    analyticsService.trackRowAction(data);
    sessionTracker.recordActivity('gridEditing');
  }, []);

  // Playback tracking
  const trackPlaybackStart = useCallback((data: PlaybackStartData) => {
    analyticsService.trackPlaybackStart(data);
    sessionTracker.recordActivity('playback');
    sessionTracker.updateMetrics({ playbackIterations: 1 });
  }, []);

  const trackPlaybackStop = useCallback((data: PlaybackStopData) => {
    analyticsService.trackPlaybackStop(data);
    sessionTracker.recordActivity('playback');
  }, []);

  const trackBPMChange = useCallback((data: BPMChangeData) => {
    analyticsService.trackBPMChange(data);
    sessionTracker.recordActivity('playback');
  }, []);

  // Composition tracking
  const trackCompositionExport = useCallback((data: CompositionExportData) => {
    analyticsService.trackCompositionExport(data);
    sessionTracker.recordActivity('gridEditing');
  }, []);

  const trackCompositionSaveLoad = useCallback((data: CompositionSaveLoadData) => {
    analyticsService.trackCompositionSaveLoad(data);
    sessionTracker.recordActivity('gridEditing');
  }, []);

  // AI tracking
  const trackAIGenerationStart = useCallback((data: AIGenerationStartData) => {
    analyticsService.trackAIGenerationStart(data);
    sessionTracker.recordActivity('aiGeneration');
  }, []);

  const trackAIGenerationComplete = useCallback((data: AIGenerationCompleteData) => {
    analyticsService.trackAIGenerationComplete(data);
    sessionTracker.recordActivity('aiGeneration');
    sessionTracker.updateMetrics({ aiGenerationsUsed: 1 });
  }, []);

  const trackAIGenerationError = useCallback((data: AIGenerationErrorData) => {
    analyticsService.trackAIGenerationError(data);
    sessionTracker.recordActivity('aiGeneration');
  }, []);

  // Mixing tracking
  const trackVolumeAdjustment = useCallback((data: VolumeAdjustmentData) => {
    analyticsService.trackVolumeAdjustment(data);
    sessionTracker.recordActivity('mixing');
  }, []);

  const trackMuteToggle = useCallback((data: MuteToggleData) => {
    analyticsService.trackMuteToggle(data);
    sessionTracker.recordActivity('mixing');
  }, []);

  // Template tracking
  const trackTemplateLoad = useCallback((data: TemplateLoadData) => {
    analyticsService.trackTemplateLoad(data);
    sessionTracker.recordActivity('templateUsage');
    sessionTracker.updateMetrics({ templatesUsed: 1 });
  }, []);

  const trackTemplateSave = useCallback((data: TemplateSaveData) => {
    analyticsService.trackTemplateSave(data);
    sessionTracker.recordActivity('templateUsage');
  }, []);

  // Privacy controls
  const setOptOut = useCallback((optOut: boolean) => {
    analyticsService.setOptOut(optOut);
  }, []);

  const getOptOutStatus = useCallback(() => {
    return analyticsService.getOptOutStatus();
  }, []);

  // Health monitoring
  const getHealthMetrics = useCallback(() => {
    return analyticsService.getHealthMetrics();
  }, []);

  return {
    startSession,
    endSession,
    trackInstrumentSelection,
    trackInstrumentChange,
    trackPresetApplication,
    trackParameterAdjustment,
    trackParameterChangeComplete,
    trackCustomParametersToggle,
    trackGridCellToggle,
    trackNotePitchChange,
    trackGridDragOperation,
    trackRowAction,
    trackPlaybackStart,
    trackPlaybackStop,
    trackBPMChange,
    trackCompositionExport,
    trackCompositionSaveLoad,
    trackAIGenerationStart,
    trackAIGenerationComplete,
    trackAIGenerationError,
    trackVolumeAdjustment,
    trackMuteToggle,
    trackTemplateLoad,
    trackTemplateSave,
    setOptOut,
    getOptOutStatus,
    getHealthMetrics,
  };
}
